import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import UnifiedNavHeader from "../../../../components/navigation/UnifiedNavHeader";
import { getBrazilianStatus } from "../../../../utils/detectBrazil";
import { nbaDashPtBR } from "../../../../components/mappings/sports-dash-mappings/nba-dash.pt-br";
import StatsPanel from "../components/StatsPanel";
import GamesView from "../components/GamesView";
import DesktopPredictionBar from "../components/DesktopPredictionBar";
import NBADesktopGameCard from "../components/NBADesktopGameCard";
import {
  STATUS_FILTERS,
  DEFAULT_LEAGUE,
  DEFAULT_SEASONS,
  DEFAULT_TIMEZONE,
  LIVE_GAMES,
  UPCOMING_GAMES,
  FINISHED_GAMES
} from "../constants/filters";
import nbaService from "../services/nbaService";
import { useQuickPrediction } from "../../../../components/dashboards/hooks/useQuickPrediction";
import QuickPickUpgradeNotice from "../../../../components/QuickPickUpgradeNotice";
import { Header } from "../../../../components/dashboards/components/shared/Header";
import { AlertCircle } from "lucide-react";
import { NBA_HEADER_COPY } from "../constants/headerContent";
import NBAOddsModal from "../NBAOddsModal";
import DesktopRailNavigation from "../components/desktop/DesktopRailNavigation";
import { StatusModal } from "../components/desktop/modals";
import useNBARailFilters from '../../../../components/dashboards/mobile/hooks/useNBARailFilters';
import { DatePickerModal } from "../../../../components/dashboards/components/shared/date-picker";
import { ensureISODate } from "../../../../components/dashboards/utils/dateUtils";

const NBADesktopDashboard = () => {
  const isBrazilian = getBrazilianStatus();

  const t = useCallback(
    (text) => (isBrazilian ? nbaDashPtBR[text] || text : text),
    [isBrazilian]
  );

  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedGameForOdds, setSelectedGameForOdds] = useState(null);
  const [predictionRequestId, setPredictionRequestId] = useState(0);
  const activePredictionRequestRef = useRef(null);
  const [predictionFlowActive, setPredictionFlowActive] = useState(false);

  const [filters, setFilters] = useState({
    date: nbaService.getDefaultGameDate(),
    status: 'all',
  });

  const loadingRef = useRef(false);

  // Handle rail filter changes
  const handleRailFiltersChange = useCallback((nextFilters) => {
    setFilters(prev => ({
      ...prev,
      date: ensureISODate(nextFilters.date, prev.date),
      status: nextFilters.status || prev.status
    }));
  }, []);

  // Initialize rail filters hook
  const {
    filters: railFilters,
    activeTab: railActiveTab,
    hasActiveFilters: railHasActiveFilters,
    primaryTabs: railPrimaryTabs,
    filterObjects: railFilterObjects,
    handleTabChange: handleRailTabChange,
    handleFilterClick,
    updateFilter: updateRailFilter,
    resetFilters: resetRailFilters,
    showDateSheet: showDateModal,
    setShowDateSheet: setShowDateModal,
    showStatusSheet: showStatusModal,
    setShowStatusSheet: setShowStatusModal
  } = useNBARailFilters({
    initialDate: filters.date,
    onFiltersChange: handleRailFiltersChange
  });

  const {
    isLoading: isPredicting,
    usageState,
    error: predictionError,
    showUpgradeNotice,
    refundNotice,
    toggleGameSelection,
    clearSelections,
    predictSelectedGames,
    setShowUpgradeNotice,
    getGamePrediction,
    isGameSelected,
    selectionCount,
    checkCachedPredictions,
    isGameFrozen,
  } = useQuickPrediction();

  const checkCachedPredictionsRef = useRef(checkCachedPredictions);
  useEffect(() => {
    checkCachedPredictionsRef.current = checkCachedPredictions;
  }, [checkCachedPredictions]);

  const prevGameIdsKeyRef = useRef(null);

  const loadGames = useCallback(async () => {
    if (loadingRef.current) return;

    loadingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      const response = await nbaService.getGamesForSeasons(
        filters.date,
        DEFAULT_SEASONS,
        DEFAULT_LEAGUE,
        null,
        DEFAULT_TIMEZONE
      );

      setGames(response.response || []);

      if (response.errors && response.errors.length > 0) {
        console.warn('Some seasons failed to load:', response.errors);
      }
    } catch (err) {
      setError(t('Failed to load games. Please try again.'));
      console.error('Failed to load games:', err);
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, [filters.date, t]);

  useEffect(() => {
    if (filters.date) {
      loadGames();
    }
  }, [filters.date, loadGames]);

  // Only depend on games; call the cached-predictions function via ref and guard by ID equality
  useEffect(() => {
    if (games.length === 0) return;

    const gameIds = games.map((game) => game.id).filter(Boolean);
    if (gameIds.length === 0) return;

    const key = gameIds.slice().sort().join(',');
    if (prevGameIdsKeyRef.current === key) {
      return;
    }
    prevGameIdsKeyRef.current = key;

    try {
      checkCachedPredictionsRef.current?.('nba', gameIds);
    } catch (err) {
      console.error('Error invoking checkCachedPredictions:', err);
    }
  }, [games]);

  const liveGames = useMemo(() => games.filter((game) => game?.status?.short === 2), [games]);
  const upcomingGames = useMemo(() => games.filter((game) => game?.status?.short === 1), [games]);
  const finishedGames = useMemo(() => games.filter((game) => game?.status?.short === 3), [games]);

  const filteredGames = useMemo(() => {
    switch (filters.status) {
      case LIVE_GAMES.value:
        return liveGames;
      case UPCOMING_GAMES.value:
        return upcomingGames;
      case FINISHED_GAMES.value:
        return finishedGames;
      default:
        // Sort by priority: Live > Upcoming > Finished
        return [...liveGames, ...upcomingGames, ...finishedGames];
    }
  }, [filters.status, games, liveGames, upcomingGames, finishedGames]);

  const stats = useMemo(
    () => ({
      total: games.length,
      live: liveGames.length,
      upcoming: upcomingGames.length,
      finished: finishedGames.length,
    }),
    [games.length, liveGames.length, upcomingGames.length, finishedGames.length]
  );

  const handleFilterChange = useCallback((key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleViewOdds = useCallback((game) => {
    setSelectedGameForOdds(game);
  }, []);

  const handleCloseOdds = useCallback(() => setSelectedGameForOdds(null), []);

  // Auto-reset selections when a prediction request finishes
  useEffect(() => {
    if (isPredicting) return;

    const shouldFinalize =
      !isPredicting &&
      !predictionFlowActive &&
      activePredictionRequestRef.current === predictionRequestId &&
      predictionRequestId > 0;

    if (shouldFinalize) {
      clearSelections();
      activePredictionRequestRef.current = null;
      setTimeout(() => clearSelections(), 600);
    }
  }, [clearSelections, isPredicting, predictionFlowActive, predictionRequestId]);

  const handlePredictRequest = useCallback(async () => {
    setPredictionRequestId((prev) => {
      const next = prev + 1;
      activePredictionRequestRef.current = next;
      return next;
    });
    setPredictionFlowActive(true);
    await predictSelectedGames("nba");
    clearSelections(); // hide bar and reset counts after submission
    setPredictionFlowActive(false);
  }, [clearSelections, predictSelectedGames]);

  const renderGameCard = useCallback(
    (game, idx) => (
      <NBADesktopGameCard
        key={idx}
        game={game}
        isSelected={isGameSelected(game)}
        isFrozen={isGameFrozen(game.id)}
        onToggleSelection={() => toggleGameSelection(game)}
        prediction={getGamePrediction(game.id)}
        onViewOdds={handleViewOdds}
      />
    ),
    [getGamePrediction, handleViewOdds, isGameFrozen, isGameSelected, toggleGameSelection]
  );

  return (
    <div className="min-h-screen bg-black text-white pb-32">
      {/*
        <SEOHead
          title={t('NBA Dashboard - Live Scores & Games | PickAI')}
          description={t('Track live NBA games, scores, and predictions with real-time updates')}
          keywords={t('nba, basketball, live scores, predictions')}
        />
      */}

      <UnifiedNavHeader
        title={t('NBA Dashboard')}
        showBackButton
      />

      <div className="relative px-6 py-8 pb-24">
        <div className="max-w-[1600px] mx-auto space-y-4">
          {/* Header */}
          <Header t={t} {...NBA_HEADER_COPY} />

          {/* Beta Notice */}
          {/*<BetaNotice t={t} />*/}

          {/* Desktop Rail Navigation - Filter Rail */}
          <DesktopRailNavigation
            sport="nba"
            primaryTabs={railPrimaryTabs}
            filters={railFilterObjects}
            activeTab={railActiveTab}
            onTabChange={handleRailTabChange}
            onFilterChange={handleFilterClick}
            onReset={resetRailFilters}
            hasActiveFilters={railHasActiveFilters}
          />

          {/* Error Message */}
          {error && (
            <div className="border border-red-500/40 rounded-sm p-4 bg-red-500/10">
              <div className="flex items-center gap-3 text-sm font-mono uppercase tracking-widest text-red-200">
                <AlertCircle className="text-red-300" size={20} />
                <p>{error}</p>
              </div>
            </div>
          )}

          <StatsPanel stats={stats} t={t} />

          <GamesView
            filters={filters}
            onFilterChange={handleFilterChange}
            loading={loading}
            onRefresh={loadGames}
            statusFilters={STATUS_FILTERS}
            filteredGames={filteredGames}
            totalGames={games.length}
            error={error}
            onRetry={loadGames}
            renderGame={renderGameCard}
            t={t}
          />
        </div>
      </div>

      <DesktopPredictionBar
        selectionCount={selectionCount}
        usageState={usageState}
        isLoading={isPredicting}
        error={predictionError}
        refundNotice={refundNotice}
        onPredict={handlePredictRequest}
        onClear={clearSelections}
        selectedGames={games.filter((g) => isGameSelected(g))}
      />

      {selectedGameForOdds && (
        <NBAOddsModal
          game={selectedGameForOdds}
          isOpen={!!selectedGameForOdds}
          onClose={handleCloseOdds}
        />
      )}

      {showUpgradeNotice && (
        <QuickPickUpgradeNotice
          onClose={() => setShowUpgradeNotice(false)}
          isMobile={false}
          sport="NBA"
        />
      )}

      {/* Desktop Filter Modals */}
      <DatePickerModal
        isOpen={showDateModal}
        onClose={() => setShowDateModal(false)}
        selectedDate={ensureISODate(railFilters.date || filters.date)}
        onDateSelect={(isoDate) => {
          const formatted = ensureISODate(isoDate, filters.date);
          updateRailFilter("date", formatted);
          setFilters((prev) => ({ ...prev, date: formatted }));
          setShowDateModal(false);
        }}
      />

      <StatusModal
        isOpen={showStatusModal}
        onClose={() => setShowStatusModal(false)}
        currentStatus={railFilters.status || 'all'}
        onStatusChange={(status) => {
          updateRailFilter('status', status);
          setShowStatusModal(false);
        }}
        sport="nba"
      />
    </div>
  );
};

export default NBADesktopDashboard;
